import { unwrap, type components } from "@sagot/api-client";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";

/** A knowledge source an article card can link: a ready website with a public https address (`GET /v1/sources?public_url=true`). */
export type ArticleSource = components["schemas"]["SourceView"];
/** One crawled page of such a website (`GET /v1/sources/{id}/pages`). */
export type ArticlePage = components["schemas"]["CrawlPageView"];

const STALE_MS = 60_000;
/** The source list the picker shows before anything is typed (and the names the step summaries read). */
const SOURCES_PAGE = 100;

export const articleKeys = {
  sources: (ws: string, query: string) => ["ws", ws, "workflows", "article-sources", query] as const,
  pages: (ws: string, sourceId: string, query: string) => ["ws", ws, "workflows", "article-pages", sourceId, query] as const,
};

/** Ready websites with a public link, newest first, optionally matching `query` in the title or the address. Needs `knowledge.view`. */
export function useArticleSources(query: string, enabled: boolean): UseQueryResult<ArticleSource[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: articleKeys.sources(ws, query),
    queryFn: () => unwrap(api.GET("/v1/sources", { params: { query: { public_url: true, status: "ready", limit: SOURCES_PAGE, ...(query ? { q: query } : {}) } } })),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

/** The pages of one crawled website a card can link (the server sends at most 50), optionally matching `query`. */
export function useCrawlPages(sourceId: string, query: string, enabled: boolean): UseQueryResult<ArticlePage[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: articleKeys.pages(ws, sourceId, query),
    queryFn: () => unwrap(api.GET("/v1/sources/{source_id}/pages", { params: { path: { source_id: sourceId }, query: query ? { q: query } : {} } })),
    enabled: enabled && sourceId !== "",
    retry: false,
    staleTime: STALE_MS,
  });
}
